import { Box, CircularProgress, Typography } from '@mui/material'
import jwtDecode from 'jwt-decode'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'

import { useAuth } from '@app/context/auth'
import {
  DeclineOrgInviteMutation,
  DeclineOrgInviteMutationVariables,
  GetOrgInviteQuery,
  GetOrgInviteQueryVariables,
} from '@app/generated/graphql'
import {
  InviteResponseForm,
  InviteResponseValue,
} from '@app/modules/invitation/components/InviteResponseForm'
import { InvitePageShell } from '@app/modules/invitation/components/InvitePageShell'
import { DECLINE_ORG_INVITE_MUTATION } from '@app/modules/invitation/queries/decline-org-invite'
import { GET_ORG_INVITE_QUERY } from '@app/modules/invitation/queries/get-org-invite'
import { userExistsInCognito } from '@app/util'

export const OrgInvitationPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { profile, logout } = useAuth()
  const [searchParams] = useSearchParams()
  const [response, setResponse] = useState<InviteResponseValue>('yes')
  const [errorOnDecode, setErrorOnDecode] = useState<boolean>(false)

  const tokenData = useMemo(() => {
    const token = searchParams.get('token') as string

    try {
      const decoded = jwtDecode<{ invite_id: string; sub: string }>(token)
      const inviteId = decoded.invite_id

      return { token, inviteId, email: decoded.sub }
    } catch (e) {
      console.log(e)
      setErrorOnDecode(true)
      return undefined
    }
  }, [searchParams])

  useEffect(() => {
    if (!profile || !tokenData?.email || profile.email === tokenData?.email)
      return

    logout()
  }, [profile, tokenData?.email, logout])

  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [{ data, error }] = useQuery<
    GetOrgInviteQuery,
    GetOrgInviteQueryVariables
  >({
    query: GET_ORG_INVITE_QUERY,
    pause: !tokenData?.token,
    context: useMemo(
      () => ({
        fetchOptions: {
          headers: { 'x-auth': `Bearer ${tokenData?.token}` },
        },
      }),
      [tokenData?.token],
    ),
  })

  const [, declineInvite] = useMutation<
    DeclineOrgInviteMutation,
    DeclineOrgInviteMutationVariables
  >(DECLINE_ORG_INVITE_MUTATION)

  const invite = (data?.invite || {}) as GetOrgInviteQuery['invite']
  const isFetching = !data && !error

  const organizationId = data?.invite?.orgId

  const handleSubmit = async () => {
    setIsLoading(true)

    if (tokenData) {
      if (response === 'yes') {
        const isUserLoggedIn = profile?.email === tokenData?.email
        const exists = isUserLoggedIn
          ? true
          : await userExistsInCognito(tokenData.email)
        const nextUrl = exists ? '/auto-login' : '/auto-register'
        const continueUrl = `/accept-org-invite/${tokenData.inviteId}?token=${tokenData.token}`
        const qs = new URLSearchParams({
          token: tokenData.token,
          continue: continueUrl,
          orgId: organizationId ?? '',
        })
        const targetUrl = isUserLoggedIn ? continueUrl : `${nextUrl}?${qs}`

        return navigate(targetUrl, {
          replace: true,
        })
      }

      setSubmitError(null)

      try {
        declineInvite(
          {
            inviteId: data?.invite?.id,
            profileId: profile?.id,
          },
          {
            fetchOptions: {
              headers: { 'x-auth': `Bearer ${tokenData.token}` },
            },
          },
        )
        return navigate('/login?invitationDeclined=true')
      } catch (e) {
        const err = e as Error
        console.log(err)
        setSubmitError('Unable to submit response')
      }

      setIsLoading(false)
    }
  }

  if (errorOnDecode) {
    return (
      <Box>
        <Typography>{t('invitation.invitation-invalid-token')}</Typography>
      </Box>
    )
  }

  if (isFetching) {
    return <CircularProgress size={40} />
  }

  if (error) {
    return (
      <Box>
        <Typography>{t('invitation.invitation-expired')}</Typography>
      </Box>
    )
  }

  return (
    <InvitePageShell>
      <Typography variant="h3" fontWeight="600" mb={3}>
        {`${t('pages.org-invite.title')} ${invite?.orgName}`}
      </Typography>

      <InviteResponseForm
        response={response}
        onResponseChange={setResponse}
        acceptLabel={t('pages.org-invite.accept')}
        declineLabel={t('pages.org-invite.decline')}
        acceptTestId="accept"
        declineTestId="decline"
        submitLabel={
          response === 'yes'
            ? t('pages.org-invite.continue-to-organization')
            : t('invitation.send-response')
        }
        submitTestId="invite-submit"
        loading={isLoading}
        error={submitError}
        onSubmit={handleSubmit}
      />
    </InvitePageShell>
  )
}
